import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

interface User {
  id: string;
  email: string;
  fullName: string;
}

interface AuthContextType {
  user: User | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<{ error: any }>;
  signUp: (email: string, password: string, fullName: string) => Promise<{ error: any }>;
  signInWithGoogle: () => Promise<{ error: any }>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Authorized email - only this email can access the app
const AUTHORIZED_EMAIL = process.env.REACT_APP_AUTHORIZED_EMAIL || 'admin@scinter.org';

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check if user is already logged in (from localStorage)
    const storedUser = localStorage.getItem('scinter_user');
    if (storedUser) {
      try {
        setUser(JSON.parse(storedUser));
      } catch (e) {
        localStorage.removeItem('scinter_user');
      }
    }
    setLoading(false);
  }, []);

  const signUp = async (email: string, password: string, fullName: string) => {
    // Sign up enabled but redirects to demo page
    // Redirect immediately to demo page
    window.location.href = '/demo';
    return { 
      error: { 
        message: 'Redirecting to demo page...',
        redirectToDemo: true
      } 
    };
  };

  const signIn = async (email: string, password: string) => {
    try {
      // Validate input
      if (!email || !password) {
        return { error: { message: 'Email and password are required' } };
      }

      // Check if email is authorized
      if (email.toLowerCase() !== AUTHORIZED_EMAIL.toLowerCase()) {
        return { 
          error: { 
            message: 'Access restricted. Please book a demo to get access.',
            redirectToDemo: true
          } 
        };
      }

      // Check credentials (only for authorized email)
      const users = JSON.parse(localStorage.getItem('scinter_users') || '[]');
      const foundUser = users.find(
        (u: any) => u.email.toLowerCase() === AUTHORIZED_EMAIL.toLowerCase() && u.password === password
      );

      if (!foundUser) {
        // If user doesn't exist, create them automatically (first time login)
        const newUser: User = {
          id: Date.now().toString(),
          email: AUTHORIZED_EMAIL,
          fullName: 'Admin User',
        };

        const userWithPassword = { ...newUser, password };
        users.push(userWithPassword);
        localStorage.setItem('scinter_users', JSON.stringify(users));

        setUser(newUser);
        localStorage.setItem('scinter_user', JSON.stringify(newUser));
        return { error: null };
      }

      // Log in the user
      const user: User = {
        id: foundUser.id,
        email: foundUser.email,
        fullName: foundUser.fullName,
      };

      setUser(user);
      localStorage.setItem('scinter_user', JSON.stringify(user));

      return { error: null };
    } catch (error: any) {
      return { error: { message: error.message || 'Sign in failed' } };
    }
  };

  const signInWithGoogle = async () => {
    // Google Sign In would require OAuth setup
    // For now, return an informative error
    return { 
      error: { 
        message: 'Google Sign In requires OAuth configuration. Please use email/password instead.' 
      } 
    };
  };

  const signOut = async () => {
    setUser(null);
    localStorage.removeItem('scinter_user');
  };

  const value = {
    user,
    loading,
    signIn,
    signUp,
    signInWithGoogle,
    signOut,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
